import { type NodeModel } from "@/models/nodes/types";

import type { CursorContext } from "@/models/CursorMapModel";

const NODE_TYPES = {
  cursor: ["number", "variable", "constant", "group", "function", "placeholder", "operatorSymbol"],
  active: ["number", "variable", "constant", "group", "function", "placeholder"],
  parent: ["number", "variable", "constant", "group", "function", "placeholder"],
} satisfies {
  cursor: readonly NodeModel["type"][];
  active: readonly NodeModel["type"][];
  parent: readonly NodeModel["type"][];
};

function getCursorNode(cursorContext: CursorContext) {
  const {
    cursor,
    selection: { activeNode, hasSelectedNodes },
  } = cursorContext;

  if (hasSelectedNodes) {
    return undefined;
  }

  if (activeNode && NODE_TYPES.active.includes(activeNode.type)) {
    return activeNode;
  }

  const { nodeBeforeCursor, node } = cursor;

  if (nodeBeforeCursor && NODE_TYPES.cursor.includes(nodeBeforeCursor.type)) {
    return nodeBeforeCursor;
  }

  if (node?.parent) {
    if (NODE_TYPES.parent.includes(node.parent.type)) {
      return node.parent;
    }

    return node.parent.ancestors.find((n) => NODE_TYPES.parent.includes(n.type));
  }

  return undefined;
}

export function getActiveNodes(
  cursorContext: CursorContext,
  filterFn?: (nodes: NodeModel) => boolean,
): NodeModel[] {
  const {
    selection: { hasSelectedNodes, activeNode },
  } = cursorContext;
  const node = getCursorNode(cursorContext);

  if (activeNode) {
    if (filterFn) {
      return [activeNode].filter(filterFn);
    }

    return [activeNode];
  }

  if (!node || hasSelectedNodes) {
    return [];
  }

  const nodes: NodeModel[] = [node];
  let currentNode: NodeModel | undefined = node.parent;

  while (currentNode) {
    if (NODE_TYPES.parent.includes(currentNode.type)) {
      nodes.push(currentNode);
    }
    currentNode = currentNode.parent;
  }

  if (filterFn) {
    return nodes.filter(filterFn);
  }

  return nodes;
}
